import { z } from "zod";
//#region src/identity-token.ts
/**
* The CP identity token wire contract (feature 2026-10-22-cp-identity-token, D2 to D8, D11, D12).
*
* The control plane signs a short RS256 JWT for one allow-listed vendor audience, so that a
* Witness operator's tool (review-agent) can exchange it at Anthropic or OpenAI through their
* federation rules. The token comes from a sibling issuer with its own Cloud KMS key, never the
* CP access-token key, so it verifies at no CP service.
*
* Four shapes cross a boundary:
*
* 1. `POST /v1/auth/token/identity`. cp-auth-api binds {@link identityTokenRequestSchema}; the
*    `zion identity-token` verb parses {@link identityTokenResponseSchema}.
* 2. The token itself. cp-auth-api signs {@link identityTokenClaimsSchema}; the vendors verify it.
* 3. The public issuer documents at `https://identity.internal.witness.ai`:
*    {@link identityIssuerDiscoverySchema} and {@link identityIssuerJwksSchema}.
* 4. The admin read `GET /v1/admin/auth-providers/identity-token`, which the CP console reads
*    through the site-admin-api proxy: {@link identityIssuerConfigSchema}.
*
* Every object schema is `.strict()` and carries no `.transform()`, so the global
* `ZionStrictValidationPipe` refuses an unknown key with a 400 and `createOpenApiDocument()`
* can represent every schema.
*/
/** The control-plane path of the mint route (D7). */
const IDENTITY_TOKEN_PATH = "/v1/auth/token/identity";
/** The control-plane path of the admin read of the issuer settings (D12). */
const IDENTITY_ISSUER_CONFIG_PATH = "/v1/admin/auth-providers/identity-token";
/** The cp-auth-api path of the issuer's OpenID discovery document; the identity gateway rewrites to it (D6). */
const IDENTITY_ISSUER_DISCOVERY_INTERNAL_PATH = "/identity/openid-configuration";
/** The cp-auth-api path of the issuer's key set; the identity gateway rewrites to it (D6). */
const IDENTITY_ISSUER_JWKS_INTERNAL_PATH = "/identity/jwks";
/** The public path of the discovery document under the issuer URL (OpenID Connect Discovery 1.0, section 4). */
const IDENTITY_ISSUER_DISCOVERY_PUBLIC_PATH = "/.well-known/openid-configuration";
/** The public path of the key set under the issuer URL (D2). */
const IDENTITY_ISSUER_JWKS_PUBLIC_PATH = "/jwks";
/** The longest lifetime of a minted token, `exp - iat`, in seconds (D4). */
const IDENTITY_TOKEN_MAX_TTL_SECONDS = 300;
/** The shortest lifetime the TTL setting may name, in seconds (00-index.md, settings). */
const IDENTITY_TOKEN_MIN_TTL_SECONDS = 60;
/** The longest `audience` the mint route accepts, in characters (D7). */
const IDENTITY_TOKEN_MAX_AUDIENCE_LENGTH = 256;
/** The longest token the response may carry, in characters. A 2048-bit RS256 token is about 900. */
const IDENTITY_TOKEN_MAX_LENGTH = 8192;
/** The one signing algorithm of the issuer (D1). */
const IDENTITY_TOKEN_ALGORITHM = "RS256";
/** The audit action of one mint and one refused mint (D7). */
const IDENTITY_TOKEN_AUDIT_ACTION = "identity_token_minted";
/**
* The machine-readable error codes of the mint route. Each is the `error` member of the
* refusal body, so the CLI can print one line per code.
*/
const IDENTITY_TOKEN_ERROR_CODES = [
	"identity_token_disabled",
	"identity_token_audience_not_allowed",
	"identity_token_not_human_operator",
	"identity_token_email_domain_not_allowed",
	"identity_token_signer_unavailable"
];
/** The one HTTP status of each error code. The error schema refuses any other pair. */
const IDENTITY_TOKEN_ERROR_STATUS = {
	identity_token_disabled: 503,
	identity_token_audience_not_allowed: 400,
	identity_token_not_human_operator: 403,
	identity_token_email_domain_not_allowed: 403,
	identity_token_signer_unavailable: 503
};
/** An absolute `https:` URL. Both vendors fetch the issuer documents over HTTPS on port 443 only. */
const httpsUrl = z.string().url().refine((u) => u.startsWith("https://"), { message: "the URL must use https" });
/** A compact JWS: three base64url segments joined by two full stops. */
const COMPACT_JWT_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
/** The body of `POST /v1/auth/token/identity` (D7). The route matches `audience` exactly. */
const identityTokenRequestSchema = z.object({ audience: z.string().min(1).max(256) }).strict();
/** The 200 reply of `POST /v1/auth/token/identity` (D7). `expiresIn` is in seconds. */
const identityTokenResponseSchema = z.object({
	token: z.string().min(1).max(IDENTITY_TOKEN_MAX_LENGTH).regex(COMPACT_JWT_PATTERN),
	expiresIn: z.number().int().min(1).max(300)
}).strict();
/** The refusal body of the mint route. `message` is one line the CLI prints as it is. */
const identityTokenErrorSchema = z.object({
	statusCode: z.number().int().min(400).max(599),
	error: z.enum(IDENTITY_TOKEN_ERROR_CODES),
	message: z.string().min(1).max(512).regex(/^[^\r\n]+$/)
}).strict().refine((e) => IDENTITY_TOKEN_ERROR_STATUS[e.error] === e.statusCode, {
	message: "the status must be the one that IDENTITY_TOKEN_ERROR_STATUS names for the code",
	path: ["statusCode"]
});
/**
* The claims of a minted token (D4). The schema refuses every CP permission claim by
* `.strict()`: no `siteRoles`, `sitePermissions`, `scope`, `sessionType`, `amr`,
* `email_verified` or `groups`. `sub` is the CP operator id.
*/
const identityTokenClaimsSchema = z.object({
	iss: httpsUrl,
	aud: z.string().min(1).max(256),
	sub: z.uuid(),
	email: z.email(),
	iat: z.number().int().nonnegative(),
	exp: z.number().int().positive(),
	jti: z.uuid()
}).strict().refine((c) => c.exp > c.iat && c.exp - c.iat <= 300, {
	message: `exp - iat must be between 1 and 300 seconds`,
	path: ["exp"]
});
/** The claim names the discovery document lists in `claims_supported` (D4). */
const IDENTITY_TOKEN_CLAIM_NAMES = [
	"iss",
	"aud",
	"sub",
	"email",
	"iat",
	"exp",
	"jti"
];
/**
* The issuer's OpenID discovery document (D2, D6). Both vendors read `issuer` and `jwks_uri`
* (`research/vendor-federation.md`). The issuer serves no authorization or token endpoint.
*/
const identityIssuerDiscoverySchema = z.object({
	issuer: httpsUrl,
	jwks_uri: httpsUrl,
	response_types_supported: z.tuple([z.literal("id_token")]),
	subject_types_supported: z.tuple([z.literal("public")]),
	id_token_signing_alg_values_supported: z.tuple([z.literal(IDENTITY_TOKEN_ALGORITHM)]),
	claims_supported: z.array(z.enum(IDENTITY_TOKEN_CLAIM_NAMES))
}).strict().refine((d) => d.claims_supported.length === IDENTITY_TOKEN_CLAIM_NAMES.length && IDENTITY_TOKEN_CLAIM_NAMES.every((c) => d.claims_supported.includes(c)), {
	message: "claims_supported must list every claim the issuer emits, once",
	path: ["claims_supported"]
});
/**
* One public RSA key of the issuer (D5). `.strict()` refuses every private member (`d`, `p`,
* `q`, `dp`, `dq`, `qi`), so a key set that would leak a private half fails the schema.
* `kid` is the RFC 7638 SHA-256 thumbprint, base64url, 43 characters.
*/
const identityIssuerPublicJwkSchema = z.object({
	kty: z.literal("RSA"),
	use: z.literal("sig"),
	alg: z.literal(IDENTITY_TOKEN_ALGORITHM),
	kid: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
	n: z.string().regex(/^[A-Za-z0-9_-]+$/).min(1),
	e: z.string().regex(/^[A-Za-z0-9_-]+$/).min(1)
}).strict();
/** The issuer's key set (D5). It holds one key per published KMS version, and never none. */
const identityIssuerJwksSchema = z.object({ keys: z.array(identityIssuerPublicJwkSchema).min(1) }).strict().refine((s) => new Set(s.keys.map((k) => k.kid)).size === s.keys.length, {
	message: "every kid in the key set must be unique",
	path: ["keys"]
});
/** One published KMS key version as the admin read shows it (D5, D11). No key material. */
const identityIssuerKeyVersionSchema = z.object({
	/** The full KMS resource name, `projects/.../cryptoKeyVersions/<n>`; empty for the local software signer. */
	name: z.string().max(512),
	/** The RFC 7638 thumbprint, or null when the public key could not be read. */
	kid: z.string().regex(/^[A-Za-z0-9_-]{43}$/).nullable(),
	/** True for the first version of the list, the one that signs. */
	signing: z.boolean()
}).strict();
/**
* The admin read of the issuer settings (D11, D12). `enabled: false` when the issuer setting is
* absent; then every URL is null and the lists are empty. The read carries settings only.
*/
const identityIssuerConfigSchema = z.object({
	enabled: z.boolean(),
	issuer: httpsUrl.nullable(),
	discoveryUrl: httpsUrl.nullable(),
	jwksUrl: httpsUrl.nullable(),
	ttlSeconds: z.number().int().min(0).max(300),
	signer: z.enum(["kms", "software"]).nullable(),
	audiences: z.array(z.string().min(1).max(256)),
	emailDomains: z.array(z.string().min(1).max(253)),
	keyVersions: z.array(identityIssuerKeyVersionSchema)
}).strict().superRefine((c, ctx) => {
	const issue = (path, message) => ctx.addIssue({
		code: "custom",
		path: [path],
		message
	});
	if (!c.enabled) {
		if (c.issuer !== null || c.discoveryUrl !== null || c.jwksUrl !== null) issue("issuer", "a disabled issuer carries no URL");
		if (c.signer !== null) issue("signer", "a disabled issuer names no signer");
		if (c.ttlSeconds !== 0) issue("ttlSeconds", "a disabled issuer has a TTL of 0");
		if (c.audiences.length + c.emailDomains.length + c.keyVersions.length > 0) issue("audiences", "a disabled issuer lists nothing");
		return;
	}
	if (c.issuer === null || c.discoveryUrl === null || c.jwksUrl === null) issue("issuer", "an enabled issuer carries all three URLs");
	if (c.signer === null) issue("signer", "an enabled issuer names its signer");
	if (c.ttlSeconds < 60) issue("ttlSeconds", `an enabled issuer has a TTL of at least 60 s`);
	if (c.keyVersions.length === 0) issue("keyVersions", "an enabled issuer publishes at least one key version");
	if (c.keyVersions.some((v, i) => v.signing !== (i === 0))) issue("keyVersions", "the first key version signs, and only the first");
});
//#endregion
export { IDENTITY_ISSUER_CONFIG_PATH, IDENTITY_ISSUER_DISCOVERY_INTERNAL_PATH, IDENTITY_ISSUER_DISCOVERY_PUBLIC_PATH, IDENTITY_ISSUER_JWKS_INTERNAL_PATH, IDENTITY_ISSUER_JWKS_PUBLIC_PATH, IDENTITY_TOKEN_ALGORITHM, IDENTITY_TOKEN_AUDIT_ACTION, IDENTITY_TOKEN_CLAIM_NAMES, IDENTITY_TOKEN_ERROR_CODES, IDENTITY_TOKEN_ERROR_STATUS, IDENTITY_TOKEN_MAX_AUDIENCE_LENGTH, IDENTITY_TOKEN_MAX_LENGTH, IDENTITY_TOKEN_MAX_TTL_SECONDS, IDENTITY_TOKEN_MIN_TTL_SECONDS, IDENTITY_TOKEN_PATH, identityIssuerConfigSchema, identityIssuerDiscoverySchema, identityIssuerJwksSchema, identityIssuerKeyVersionSchema, identityIssuerPublicJwkSchema, identityTokenClaimsSchema, identityTokenErrorSchema, identityTokenRequestSchema, identityTokenResponseSchema };

//# sourceMappingURL=identity-token.js.map